import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, BellSlash as BellOff, CreditCard, EnvelopeSimple, Eye, EyeSlash, Receipt, PaperPlaneTilt as Send, Table, User, WarningCircle, ArrowsClockwise, ChatCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { CaddyErrorCard } from "../components/CaddyErrorCard";
import { CaddySidebar } from "../components/CaddySidebar";
import { useSheetReconnect } from "../hooks/useSheetReconnect";
import { CaddyBottomNav } from "../components/CaddyBottomNav";
import { RelayLoader } from "../components/RelayLoader";
import { disablePush, enablePush, needsInstallFirst, pushSupported, sendTestPush } from "../lib/push";
import { homeSection } from '../lib/marketingHome';
import { MASK, setPrivacy, usePrivacy } from '../lib/privacy';

const API = process.env.REACT_APP_BACKEND_URL;

const COLOR = {
  ink: "var(--pr-ink)", ink2: "var(--pr-ink2)", muted: "var(--pr-muted)",
  line: "var(--pr-line)", gold: "var(--pr-gold)", green: "var(--pr-green)", red: "#DE3C4B",
};

const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const h = Math.floor(i / 4);
  const m = (i % 4) * 15;
  const value = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return { value, label: `${hour12}:${String(m).padStart(2, "0")} ${h < 12 ? "AM" : "PM"}` };
});

const prettyDate = (iso) =>
  iso ? new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : null;

const money = (n) => (n == null ? null : `$${Number(n).toFixed(2)}`);  // billing, never masked

const Section = ({ icon: Icon, title, subtitle, children, testid }) => (
  <section className="rounded-2xl p-5 lg:p-6 pr-card mb-5" data-testid={testid}>
    <div className="flex items-start gap-3 mb-4">
      <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "var(--pr-bg)" }}>
        <Icon className="w-4 h-4" style={{ color: COLOR.ink }} />
      </span>
      <div>
        <h2 className="text-base font-bold" style={{ color: COLOR.ink }}>{title}</h2>
        {subtitle && <p className="text-xs leading-relaxed mt-0.5" style={{ color: COLOR.ink2 }}>{subtitle}</p>}
      </div>
    </div>
    {children}
  </section>
);

const Switch = ({ on, onClick, testid, label }) => (
  <button
    onClick={onClick}
    role="switch"
    aria-checked={on}
    aria-label={label}
    className="relative w-11 h-6 rounded-full flex-shrink-0 transition-colors"
    style={{ background: on ? COLOR.green : "var(--pr-line)" }}
    data-testid={testid}
  >
    <span
      className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform"
      style={{ left: 2, transform: on ? "translateX(20px)" : "translateX(0)" }}
    />
  </button>
);

export default function SettingsPage() {
  const navigate = useNavigate();
  const privacyOn = usePrivacy();
  const [research, setResearch] = useState(false);
  const [emails, setEmails] = useState({ progress: true, product_news: true, news: true, beta_pulse: true, unsubscribed_all: false });
  const [view, setView] = useState("loading");
  const [data, setData] = useState(null);
  const [time, setTime] = useState("19:00");
  const [sunday, setSunday] = useState(true);
  const [emailReminders, setEmailReminders] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [nameSaving, setNameSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [discOpen, setDiscOpen] = useState(false);
  const [discBusy, setDiscBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [portalBusy, setPortalBusy] = useState(false);
  const [coach, setCoach] = useState({ progress_enabled: false, nudges_enabled: true });
  const reconnect = useSheetReconnect();

  const hydrate = (d) => {
    setData(d);
    setTime(d.notifications.checkin_time || "19:00");
    setSunday(!!d.notifications.sunday_nudge);
    setEmailReminders(!!d.notifications.email_reminders);
    setName(d.preferred_name || "");
    setResearch(!!d.preferences?.research_opt_in);
    if (d.preferences?.emails) setEmails(d.preferences.emails);
    if (!!d.preferences?.privacy_mode !== privacyOn) setPrivacy(!!d.preferences?.privacy_mode, { persist: false });
  };

  const saveResearch = async () => {
    const next = !research;
    setResearch(next);
    try {
      const res = await fetch(`${API}/api/settings/preferences`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ research_opt_in: next, research_consent_version: "2026-10-research-v1" }),
      });
      if (!res.ok) throw new Error();
      toast.success(next ? "Thank you, your cleared relays now count anonymously." : "Research contribution turned off.");
    } catch {
      setResearch(!next);
      toast.error("Couldn't save that, try again.");
    }
  };

  const saveEmailPref = async (key, next) => {
    const before = emails;
    setEmails({ ...emails, [key]: next });
    try {
      const res = await fetch(`${API}/api/settings/preferences`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: next }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      if (d.emails) setEmails(d.emails);
      toast.success("Email preferences saved.");
    } catch {
      setEmails(before);
      toast.error("Couldn't save that, try again.");
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/settings`, { credentials: "include" });
        if (res.status === 401) { navigate("/login?next=/app/settings", { replace: true }); return; }
        if (res.status === 403) { setView("not_member"); return; }
        if (!res.ok) { setView("error"); return; }
        hydrate(await res.json());
        setView("ready");
      } catch {
        setView("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/caddy/coach-prefs`, { credentials: "include" });
        if (res.ok) setCoach(await res.json());
      } catch { /* noop */ }
    })();
  }, []);

  const saveCoach = async (key, next) => {
    const before = coach;
    setCoach({ ...coach, [key]: next });
    try {
      const res = await fetch(`${API}/api/caddy/coach-prefs`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [key]: next }),
      });
      if (!res.ok) throw new Error();
      setCoach(await res.json());
      toast.success("Saved.");
    } catch {
      setCoach(before);
      toast.error("Couldn't save that, try again.");
    }
  };

  const saveReminders = async (overrides = {}) => {
    setSaving(true);
    try {
      const body = {
        checkin_time: time,
        sunday_nudge: sunday,
        email_reminders: emailReminders,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        ...overrides,
      };
      const res = await fetch(`${API}/api/settings/notifications`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Couldn't save your reminder settings."); return; }
      setData((prev) => ({ ...prev, notifications: d.notifications }));
      toast.success("Reminder settings saved.");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setSaving(false);
    }
  };

  const togglePush = async () => {
    setPushBusy(true);
    try {
      if (data.notifications.push_enabled) {
        await disablePush();
        setData((p) => ({ ...p, notifications: { ...p.notifications, push_enabled: false, devices: 0 } }));
        toast.success("Reminders paused on this device.");
      } else {
        await enablePush();
        setData((p) => ({ ...p, notifications: { ...p.notifications, push_enabled: true, devices: (p.notifications.devices || 0) + 1 } }));
        await saveReminders();
        toast.success("Reminders on, you'll hear from Caddy at your chosen time.");
      }
    } catch (e) {
      toast.error(e.message || "Could not change your reminder setting.");
    } finally {
      setPushBusy(false);
    }
  };

  const testPush = async () => {
    try {
      await sendTestPush();
      toast.success("Sent, watch for the notification.");
    } catch (e) {
      toast.error(e.message || "Could not send a test.");
    }
  };

  const disconnectGoogle = async () => {
    if (discBusy) return;
    setDiscBusy(true);
    try {
      const res = await fetch(`${API}/api/sheet-setup/google/disconnect`, {
        method: "POST",
        credentials: "include",
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Couldn't disconnect Google just now."); return; }
      setData((p) => ({ ...p, google: { ...p.google, connected: false, account: "" } }));
      setDiscOpen(false);
      toast.success("Google disconnected. Your sheet stays in your Drive.");
    } catch {
      toast.error("Couldn't reach the server. Please try again.");
    } finally {
      setDiscBusy(false);
    }
  };


  const saveName = async () => {
    const trimmed = name.trim();
    if (!trimmed || nameSaving) return;
    setNameSaving(true);
    try {
      const res = await fetch(`${API}/api/caddy/preferred-name`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: trimmed }),
      });
      const d = await res.json();
      if (!res.ok) { toast.error(d.detail || "Couldn't save that name."); return; }
      setData((p) => ({ ...p, preferred_name: d.preferred_name }));
      toast.success("Saved, that's how your dashboard will greet you.");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setNameSaving(false);
    }
  };

  const openPortal = async (flow) => {
    setPortalBusy(true);
    try {
      const res = await fetch(`${API}/api/settings/billing-portal`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ flow: flow || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.url) { window.location.href = d.url; return; }
      toast.error("Couldn't open billing, email support@paydayrelay.com");
    } catch {
      toast.error("Couldn't open billing, email support@paydayrelay.com");
    } finally {
      setPortalBusy(false);
    }
  };

  const confirmCancel = async () => {
    setCancelBusy(true);
    try {
      const res = await fetch(`${API}/api/settings/cancel`, { method: "POST", credentials: "include" });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(d.detail?.message || "Couldn't cancel just now, try the billing portal.");
        return;
      }
      setCancelOpen(false);
      setData((p) => ({
        ...p,
        membership: { ...p.membership, cancel_at_period_end: d.mode === "scheduled", ends_on: d.ends_on || p.membership.ends_on, cancel_requested_at: new Date().toISOString() },
      }));
      toast.success(d.mode === "scheduled"
        ? `Cancelled. Your access runs through ${prettyDate(d.ends_on) || "the end of this billing period"}.`
        : d.message || "Request sent to support.");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setCancelBusy(false);
    }
  };

  const resume = async () => {
    setCancelBusy(true);
    try {
      const res = await fetch(`${API}/api/settings/resume`, { method: "POST", credentials: "include" });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail?.message || "Couldn't restart your membership."); return; }
      setData((p) => ({ ...p, membership: { ...p.membership, cancel_at_period_end: false, cancel_requested_at: null } }));
      toast.success("You're back in the race 🏁");
    } catch {
      toast.error("Network hiccup, try again.");
    } finally {
      setCancelBusy(false);
    }
  };

  const signOut = async () => {
    try { await fetch(`${API}/api/auth/logout`, { method: "POST", credentials: "include" }); } catch { /* noop */ }
    navigate("/app");
  };

  if (view === "error") return <CaddyErrorCard />;

  if (view === "loading") {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--pr-bg)" }} data-testid="settings-loading">
        <RelayLoader label="Opening your settings…" />
      </div>
    );
  }

  if (view === "not_member") {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--pr-bg)" }}>
        <div className="max-w-sm text-center" data-testid="settings-join-gate">
          <h1 className="text-2xl mb-3" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
            Settings ride with Payday Relay
          </h1>
          <Link to={homeSection('season-pass')} className="inline-block px-8 py-3 rounded-full font-bold" style={{ background: "var(--pr-ink-surface)", color: "#fff" }}>
            See membership options →
          </Link>
        </div>
      </div>
    );
  }

  const n = data.notifications;
  const m = data.membership;
  const b = data.billing;
  const g = data.google || { connected: false };
  const installFirst = needsInstallFirst();
  const remindersLive = n.push_enabled || emailReminders;

  return (
    <div className="min-h-screen" style={{ background: "var(--pr-bg)" }}>
      <header className="px-5 pt-5 pb-2 lg:hidden">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <Link to="/app/more" className="flex items-center gap-1.5 text-sm hover:opacity-70" style={{ color: COLOR.ink }} data-testid="settings-back-link">
            <ArrowLeft className="w-4 h-4" /> More
          </Link>
          <p className="text-[10px] font-bold tracking-widest uppercase" style={{ color: COLOR.muted }}>Settings</p>
        </div>
      </header>

      <div className="w-full max-w-7xl mx-auto lg:grid lg:grid-cols-[220px_minmax(0,1fr)] lg:gap-8 lg:px-8 lg:pt-8">
        <CaddySidebar active="settings" sheetUrl={null} onSignOut={signOut} />

        <main className="px-5 py-6 lg:px-0 lg:pt-0 pb-16">
          <div className="max-w-md mx-auto lg:max-w-2xl lg:mx-0" data-testid="settings-page">
            <h1 className="text-3xl lg:text-4xl mb-2 text-center lg:text-left" style={{ fontFamily: "'Fraunces', Georgia, serif", color: COLOR.ink }}>
              Settings
            </h1>
            <p className="text-sm text-center lg:text-left mb-6 leading-relaxed" style={{ color: COLOR.ink2 }}>
              Reminders, membership and account.
            </p>

            {/* ── Reminders ── */}
            <Section
              icon={remindersLive ? Bell : BellOff}
              title="Log reminders"
              subtitle="One nudge a day, at your time. Never on a day you've logged or tagged off."
              testid="settings-reminders"
            >
              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Phone / browser notification</p>
                  <p className="text-[11px]" style={{ color: COLOR.muted }} data-testid="settings-push-status">
                    {n.push_enabled ? `On · ${n.devices} device${n.devices === 1 ? "" : "s"}` : "Off"}
                  </p>
                </div>
                {installFirst && !n.push_enabled ? (
                  <p className="text-[11px] max-w-[55%] leading-relaxed" style={{ color: COLOR.ink2 }} data-testid="settings-push-ios-note">
                    On iPhone, add Payday Relay to your home screen first (Share → Add to Home Screen), then switch this on from there.
                  </p>
                ) : pushSupported() ? (
                  <Switch on={!!n.push_enabled} onClick={pushBusy ? () => {} : togglePush} testid="settings-push-toggle" label="Phone notifications" />
                ) : (
                  <p className="text-[11px]" style={{ color: COLOR.muted }} data-testid="settings-push-unsupported">Not supported in this browser</p>
                )}
              </div>

              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Email me instead</p>
                  <p className="text-[11px]" style={{ color: COLOR.muted }}>
                    Only when notifications are off.
                  </p>
                </div>
                <Switch on={emailReminders} onClick={() => setEmailReminders((v) => !v)} testid="settings-email-toggle" label="Email reminders" />
              </div>

              <div className="py-3 border-t" style={{ borderColor: COLOR.line }}>
                <label className="block text-sm font-semibold mb-1" style={{ color: COLOR.ink }} htmlFor="settings-checkin-time">
                  Remind me at
                </label>
                <p className="text-[11px] mb-2" style={{ color: COLOR.muted }}>
                  Your time zone: {n.timezone || "UTC"}
                </p>
                <select
                  id="settings-checkin-time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  className="w-full max-w-[180px] px-3 py-2 rounded-xl border text-sm"
                  style={{ borderColor: COLOR.line, color: COLOR.ink, background: "var(--pr-bg)" }}
                  data-testid="settings-checkin-time"
                >
                  {TIME_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Sunday close-out nudge</p>
                  <p className="text-[11px]" style={{ color: COLOR.muted }}>Sunday morning: your relay closes today.</p>
                </div>
                <Switch on={sunday} onClick={() => setSunday((v) => !v)} testid="settings-sunday-toggle" label="Sunday nudge" />
              </div>

              <div className="flex items-center gap-2 pt-4 border-t" style={{ borderColor: COLOR.line }}>
                <button
                  onClick={() => saveReminders()}
                  disabled={saving}
                  className="px-5 py-2 rounded-full font-bold text-sm disabled:opacity-40 transition-transform hover:scale-[1.02]"
                  style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                  data-testid="settings-reminders-save"
                >
                  {saving ? "Saving…" : "Save reminders"}
                </button>
                {n.push_enabled && (
                  <button
                    onClick={testPush}
                    className="text-xs font-semibold px-3 py-2 rounded-full inline-flex items-center gap-1.5"
                    style={{ color: COLOR.ink2, border: `1px solid ${COLOR.line}` }}
                    data-testid="settings-push-test"
                  >
                    <Send className="w-3 h-3" /> Send a test
                  </button>
                )}
              </div>
            </Section>

            {/* ── Privacy & research ── */}
            <Section
              icon={privacyOn ? EyeSlash : Eye}
              title="Privacy & research"
              subtitle="Hide your numbers while recording, and choose whether your cleared relays feed anonymous research."
              testid="settings-privacy"
            >
              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Creator Privacy Mode</p>
                  <p className="text-[11px] leading-relaxed" style={{ color: COLOR.ink2 }}>
                    Masks every dollar amount across the app as {MASK}. Progress bars and percentages
                    stay live, so you can film or screen-share safely.
                  </p>
                </div>
                <Switch
                  on={privacyOn}
                  onClick={() => setPrivacy(!privacyOn)}
                  testid="settings-privacy-toggle"
                  label="Creator Privacy Mode"
                />
              </div>
              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Research contribution <span className="font-normal" style={{ color: COLOR.ink2 }}>(optional)</span></p>
                  <p className="text-[11px] leading-relaxed" style={{ color: COLOR.ink2 }}>
                    Optional and separate from your beta access &mdash; turning this off changes nothing about
                    the app or your account. While it's on, your cleared relays and how pending income resolves
                    feed <b>anonymous, aggregate</b> research on independent work, motivation and money: no name,
                    no email, no exact figures or dates, only bands like $500&ndash;1,000 and 50&ndash;75% of goal.
                    The data needed to run and secure your account is kept separate and is not affected by this choice.
                    You can turn it off here any time; we then stop including your activity in future research.
                  </p>
                </div>
                <Switch
                  on={research}
                  onClick={saveResearch}
                  testid="settings-research-toggle"
                  label="Research contribution"
                />
              </div>
            </Section>

            {/* ── Caddy coaching ── */}
            <Section
              icon={ChatCircle}
              title="Caddy coaching"
              subtitle="Choose whether Caddy can read your progress, and whether it nudges you at key moments."
              testid="settings-caddy"
            >
              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Let Caddy use my relay progress <span className="font-normal" style={{ color: COLOR.ink2 }}>(off by default)</span></p>
                  <p className="text-[11px] leading-relaxed" style={{ color: COLOR.ink2 }}>
                    When this is on and you ask Caddy about your week, it's given a small summary that
                    Payday&nbsp;Relay <b>calculates from what you've logged</b>: your weekly target, what you've
                    <b> earned</b> versus actually <b>received</b>, days left, and whether a payment is past due.
                    Caddy <b>never reads your Google Sheet</b>, and the app doesn't attach your name, email or Google
                    account. Like any chat, anything you type yourself is sent so Caddy can read it. Turn this off
                    any time and Caddy answers without your numbers.
                  </p>
                </div>
                <Switch
                  on={!!coach.progress_enabled}
                  onClick={() => saveCoach("progress_enabled", !coach.progress_enabled)}
                  testid="settings-caddy-progress-toggle"
                  label="Let Caddy use my relay progress"
                />
              </div>
              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Caddy nudges</p>
                  <p className="text-[11px] leading-relaxed" style={{ color: COLOR.ink2 }}>
                    A quiet marker on the Caddy button at three moments: you hit your weekly target, you finish
                    your first relay, or a payment is past its expected date. No pop-ups, no auto-messages,
                    nothing watching you in the background. Dismiss any one, or switch them all off here.
                  </p>
                </div>
                <Switch
                  on={!!coach.nudges_enabled}
                  onClick={() => saveCoach("nudges_enabled", !coach.nudges_enabled)}
                  testid="settings-caddy-nudges-toggle"
                  label="Caddy nudges"
                />
              </div>
            </Section>

            {/* ── Email updates ── */}
            <Section
              icon={EnvelopeSimple}
              title="Email updates"
              subtitle="Choose what lands in your inbox. Billing and account notices are always sent, they're the receipts for money that moves."
              testid="settings-emails"
            >
              {[
                ["progress", "Your own progress", "Weekly relay recaps, how your race is going, and your check-in nudges."],
                ["product_news", "What's new in the app", "Short notes when something you can use actually ships."],
                ["news", "News from Payday Relay", "Occasional notes from Shari on the method and the wider story."],
                ["beta_pulse", "Beta pulse", "Beta season updates and the odd request for your feedback."],
              ].map(([key, title, body]) => (
                <div key={key} className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>{title}</p>
                    <p className="text-[11px] leading-relaxed" style={{ color: COLOR.ink2 }}>{body}</p>
                  </div>
                  <Switch
                    on={!emails.unsubscribed_all && !!emails[key]}
                    onClick={() => saveEmailPref(key, !emails[key])}
                    testid={`settings-email-${key.replace("_", "-")}-toggle`}
                    label={title}
                  />
                </div>
              ))}
              <div className="flex items-center justify-between gap-4 py-3 border-t" style={{ borderColor: COLOR.line }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLOR.ink }}>Unsubscribe from all updates</p>
                  <p className="text-[11px] leading-relaxed" style={{ color: COLOR.ink2 }}>
                    Turns off everything above in one move. You'll still get billing and account notices.
                  </p>
                </div>
                <Switch
                  on={!!emails.unsubscribed_all}
                  onClick={() => saveEmailPref("unsubscribed_all", !emails.unsubscribed_all)}
                  testid="settings-email-unsubscribe-all-toggle"
                  label="Unsubscribe from all updates"
                />
              </div>
            </Section>

            {/* ── Membership ── */}
            <Section
              icon={CreditCard}
              title="Membership & billing"
              subtitle="Payments run on Stripe. We never see your card details."
              testid="settings-membership"
            >
              <dl className="text-sm">
                <div className="flex justify-between gap-4 py-2">
                  <dt className="text-xs uppercase tracking-wider" style={{ color: COLOR.muted }}>Plan</dt>
                  <dd className="font-semibold text-right" style={{ color: COLOR.ink }} data-testid="settings-plan-label">{m.plan_label}</dd>
                </div>
                <div className="flex justify-between gap-4 py-2">
                  <dt className="text-xs uppercase tracking-wider" style={{ color: COLOR.muted }}>Status</dt>
                  <dd className="font-semibold text-right capitalize" style={{ color: m.status === "past_due" ? COLOR.red : COLOR.ink }} data-testid="settings-status">
                    {m.mode === "sprint" ? "Free Sprint" : (m.status || "active")}
                  </dd>
                </div>
                {m.mode === "sprint" && m.trial_ends_at && (
                  <div className="flex justify-between gap-4 py-2">
                    <dt className="text-xs uppercase tracking-wider" style={{ color: COLOR.muted }}>Season Pass starts</dt>
                    <dd className="font-semibold text-right" style={{ color: COLOR.ink }} data-testid="settings-trial-end">
                      {prettyDate(String(m.trial_ends_at).slice(0, 10))}
                    </dd>
                  </div>
                )}
                {m.renews_on && (
                  <div className="flex justify-between gap-4 py-2">
                    <dt className="text-xs uppercase tracking-wider" style={{ color: COLOR.muted }}>{m.cancel_at_period_end ? "Access ends" : "Next charge"}</dt>
                    <dd className="font-semibold text-right" style={{ color: COLOR.ink }} data-testid="settings-renews-on">
                      {prettyDate(m.renews_on)}{!m.cancel_at_period_end && m.amount ? ` · ${money(m.amount)}` : ""}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between gap-4 py-2">
                  <dt className="text-xs uppercase tracking-wider" style={{ color: COLOR.muted }}>Card on file</dt>
                  <dd className="font-semibold text-right" style={{ color: COLOR.ink }} data-testid="settings-card">
                    {b.card ? `${b.card.brand?.toUpperCase()} •••• ${b.card.last4} · exp ${b.card.exp_month}/${String(b.card.exp_year).slice(-2)}` : "Managed in the billing portal"}
                  </dd>
                </div>
              </dl>

              <div className="flex flex-wrap gap-2 mt-4">
                <button
                  onClick={() => openPortal("payment_method")}
                  disabled={portalBusy}
                  className="text-xs font-semibold px-4 py-2.5 rounded-lg disabled:opacity-50"
                  style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                  data-testid="settings-update-card-btn"
                >
                  {portalBusy ? "Opening…" : "Update payment method"}
                </button>
                <button
                  onClick={() => openPortal("invoices")}
                  disabled={portalBusy}
                  className="text-xs font-semibold px-4 py-2.5 rounded-lg inline-flex items-center gap-1.5 disabled:opacity-50"
                  style={{ color: COLOR.ink, border: `1px solid ${COLOR.line}` }}
                  data-testid="settings-portal-btn"
                >
                  <Receipt className="w-3.5 h-3.5" /> Billing portal
                </button>
              </div>

              {b.invoices?.length > 0 && (
                <div className="mt-5 pt-4 border-t" style={{ borderColor: COLOR.line }} data-testid="settings-invoices">
                  <p className="text-xs font-bold uppercase tracking-widest mb-2" style={{ color: COLOR.muted }}>Billing history</p>
                  <ul className="space-y-1.5">
                    {b.invoices.map((inv, i) => (
                      <li key={inv.id || i} className="flex items-center justify-between gap-3 text-sm" data-testid={`settings-invoice-${i}`}>
                        <span style={{ color: COLOR.ink2 }}>{prettyDate(inv.date)}</span>
                        <span className="flex items-center gap-3">
                          <span className="font-semibold" style={{ color: COLOR.ink }}>{money(inv.amount)}</span>
                          {inv.url && (
                            <a href={inv.url} target="_blank" rel="noreferrer" className="text-[11px] underline" style={{ color: COLOR.ink2 }}>
                              receipt
                            </a>
                          )}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 pt-4 border-t" style={{ borderColor: COLOR.line }}>
                {m.cancel_at_period_end || m.cancel_requested_at ? (
                  <div className="rounded-lg p-4 border" style={{ borderColor: COLOR.line, background: "var(--pr-bg)" }} data-testid="settings-cancel-notice">
                    <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>
                      {m.cancel_at_period_end
                        ? `Your membership ends ${prettyDate(m.ends_on) || "at the end of this billing period"}`
                        : "Your cancellation request is with support"}
                    </p>
                    <p className="text-[11px] leading-relaxed mb-3" style={{ color: COLOR.ink2 }}>
                      Nothing changes until then, keep logging, and your Google Sheet stays yours either way.
                    </p>
                    <button
                      onClick={resume}
                      disabled={cancelBusy}
                      className="px-4 py-2 rounded-lg font-semibold text-xs disabled:opacity-40"
                      style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                      data-testid="settings-resume-btn"
                    >
                      {cancelBusy ? "Working…" : "Rejoin the race 🏁"}
                    </button>
                  </div>
                ) : cancelOpen ? (
                  <div className="rounded-lg p-4 border" style={{ borderColor: COLOR.line, background: "var(--pr-bg)" }} data-testid="settings-cancel-panel">
                    <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>Before you go. Your sheet stays yours, forever</p>
                    <p className="text-[11px] leading-relaxed mb-3" style={{ color: COLOR.ink2 }}>
                      Your sheet lives in your own Drive and stays exactly as it is, the app just stops syncing
                      to it. Your access runs to the end of the period you've paid for.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={confirmCancel}
                        disabled={cancelBusy}
                        className="px-4 py-2 rounded-lg font-semibold text-xs disabled:opacity-40"
                        style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                        data-testid="settings-cancel-confirm-btn"
                      >
                        {cancelBusy ? "Cancelling…" : "Cancel my membership"}
                      </button>
                      <button
                        onClick={() => setCancelOpen(false)}
                        className="px-4 py-2 rounded-lg font-semibold text-xs"
                        style={{ color: COLOR.ink2, border: `1px solid ${COLOR.line}` }}
                        data-testid="settings-cancel-dismiss"
                      >
                        Never mind, staying in
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setCancelOpen(true)}
                    className="text-xs font-semibold underline"
                    style={{ color: COLOR.muted }}
                    data-testid="settings-cancel-btn"
                  >
                    Cancel my membership
                  </button>
                )}
              </div>
            </Section>

            {/* ── Google connection ── */}
            <Section icon={Table} title="Google Sheet" testid="settings-google">
              <div className="py-2.5 border-t" style={{ borderColor: COLOR.line }}>
                <p className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color: COLOR.muted }}>Connection</p>
                <p className="text-sm font-semibold" style={{ color: COLOR.ink }} data-testid="settings-google-status">
                  {g.connected ? `Connected${g.account ? ` as ${g.account}` : ""}` : "Not connected"}
                </p>
                <p className="text-[11px] mt-1 leading-relaxed" style={{ color: COLOR.ink2 }}>
                  Payday Relay only touches the one spreadsheet it built for you. Nothing else in your
                  Google account. <Link to="/privacy#google-data" className="underline" data-testid="settings-google-privacy">How we use Google data</Link>
                </p>
              </div>
              {reconnect.needsReconnect && (
                <div className="py-3 border-t" style={{ borderColor: COLOR.line }} data-testid="settings-google-reconnect-notice">
                  <div className="rounded-lg p-4 flex items-start gap-3" style={{ background: "var(--pr-tint-gold)", border: "1px solid var(--pr-line)" }}>
                    <WarningCircle className="w-5 h-5 flex-shrink-0 mt-0.5" weight="fill" style={{ color: COLOR.red }} />
                    <div className="flex-1">
                      <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>Action needed — your Google Sheet has stopped syncing</p>
                      <p className="text-[11px] mb-3 leading-relaxed" style={{ color: COLOR.ink2 }}>
                        Reconnect once and keep the <b>“See, edit, create and delete all your Google Sheets spreadsheets”</b> box
                        ticked. Your wins are safe here and the sheet catches up.
                      </p>
                      {reconnect.reconnectPath && (
                        <a
                          href={`${API}${reconnect.reconnectPath}&ret=${encodeURIComponent("/app/settings")}`}
                          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full font-semibold text-xs"
                          style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                          data-testid="settings-google-reconnect-btn"
                        >
                          <ArrowsClockwise className="w-3.5 h-3.5" /> Reconnect Google
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
              {g.sheet_url ? (
                <div className="py-3 border-t" style={{ borderColor: COLOR.line }}>
                  <a href={g.sheet_url} target="_blank" rel="noreferrer" className="text-xs font-semibold underline"
                     style={{ color: COLOR.ink }} data-testid="settings-google-sheet-link">
                    Open my Google Sheet
                  </a>
                </div>
              ) : null}
              <div className="pt-3 border-t" style={{ borderColor: COLOR.line }}>
                {!g.connected ? (
                  g.setup_token ? (
                    <a href={`/sheet-setup?token=${g.setup_token}`} className="text-xs font-semibold underline"
                       style={{ color: COLOR.ink }} data-testid="settings-google-connect">
                      Connect Google and build my sheet
                    </a>
                  ) : (
                    <p className="text-[11px]" style={{ color: COLOR.muted }}>Your sheet setup link is in your welcome email.</p>
                  )
                ) : discOpen ? (
                  <div className="rounded-lg p-4 border" style={{ borderColor: COLOR.line, background: "var(--pr-bg)" }} data-testid="settings-google-disconnect-notice">
                    <p className="text-sm font-bold mb-1" style={{ color: COLOR.ink }}>Hand Google access back?</p>
                    <p className="text-[11px] mb-3 leading-relaxed" style={{ color: COLOR.ink2 }}>
                      We revoke our access and delete the tokens we hold. Your spreadsheet stays in your
                      Drive exactly as it is, the app just stops filling it in. You can reconnect any time.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={disconnectGoogle}
                        disabled={discBusy}
                        className="px-4 py-2 rounded-lg font-semibold text-xs disabled:opacity-40"
                        style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                        data-testid="settings-google-disconnect-confirm"
                      >
                        {discBusy ? "Disconnecting…" : "Disconnect Google"}
                      </button>
                      <button
                        onClick={() => setDiscOpen(false)}
                        className="px-4 py-2 rounded-lg font-semibold text-xs"
                        style={{ color: COLOR.ink2, border: `1px solid ${COLOR.line}` }}
                        data-testid="settings-google-disconnect-dismiss"
                      >
                        Keep it connected
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => setDiscOpen(true)}
                    className="text-xs font-semibold underline"
                    style={{ color: COLOR.muted }}
                    data-testid="settings-google-disconnect-btn"
                  >
                    Disconnect Google
                  </button>
                )}
              </div>
            </Section>

            {/* ── Account ── */}
            <Section icon={User} title="Account" testid="settings-account">
              <div className="py-2.5 border-t" style={{ borderColor: COLOR.line }}>
                <p className="text-[11px] uppercase tracking-widest font-bold mb-1" style={{ color: COLOR.muted }}>Signed in as</p>
                <p className="text-sm font-semibold" style={{ color: COLOR.ink }} data-testid="settings-email">{data.email}</p>
              </div>
              <div className="py-3 border-t" style={{ borderColor: COLOR.line }}>
                <label className="block text-sm font-semibold mb-1" style={{ color: COLOR.ink }} htmlFor="settings-name-input">
                  What should we call you?
                </label>
                <div className="flex gap-2">
                  <input
                    id="settings-name-input"
                    type="text"
                    maxLength={40}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Alex"
                    className="flex-1 max-w-[220px] px-3 py-2 rounded-xl border text-sm"
                    style={{ borderColor: COLOR.line, color: COLOR.ink, background: "var(--pr-bg)" }}
                    data-testid="settings-name-input"
                  />
                  <button
                    onClick={saveName}
                    disabled={nameSaving || !name.trim()}
                    className="px-4 py-2 rounded-full font-bold text-xs disabled:opacity-40"
                    style={{ background: "var(--pr-ink-surface)", color: "#fff" }}
                    data-testid="settings-name-save"
                  >
                    {nameSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
              <div className="pt-3 border-t" style={{ borderColor: COLOR.line }}>
                <button
                  onClick={signOut}
                  className="text-xs font-semibold underline"
                  style={{ color: COLOR.muted }}
                  data-testid="settings-signout-btn"
                >
                  Sign out
                </button>
              </div>
            </Section>
          </div>
        </main>
      </div>
      <div className="h-20 lg:hidden" />
      <CaddyBottomNav active="more" />
    </div>
  );
}
